import * as React from "react";
import { Component, CSSProperties } from "react";
import Button from "Components/Buttons/Button";
import ModalPanel from "Panels/ModalPanel";
import SensorSelectPanel from "../Primary/SensorSelectPanel";
import ButtonsFormFooter from "../../../Forms/Shared/ButtonsFormFooter";
import CancelButton from "../../../Components/Buttons/Composite/CancelButton";
import { Sensor, Sensorgroup } from "@inductosense/typescript-fetch";
import AddIcon from "../../../Components/Graphics/Icons/AddIcon";

interface SensorSelectModalPanelProps {
    rootSensorGroup: Sensorgroup;
    shouldBeShown: boolean;
    onClose(): void;
    onSensorSelect(sensors: Sensor[]): void;
    multiSelect?: true | undefined;
    title?: string;
    okLabel?: string;
    hideNoSensorOption?: boolean;
    allowedRfidPrefixes?: string[];
    deviceSerialNumber?: string;
    selectedIds?: string[]; // Add this property to receive pre-selected sensor IDs
    deviceCompatibilityMessage?: string; // Message to display about device-specific sensor filtering
}

interface SensorSelectModalPanelState {
    selectedSensors: Sensor[];
}

const containerStyle: CSSProperties = { display: "flex", margin: 10 };
const rightHandButtonsStyle: CSSProperties = { flex: 1 };
const sensorCountStyle: CSSProperties = {
    padding: "10px 15px",
    borderBottom: "1px solid #eee",
    fontSize: "14px"
};
const sensorLimitExceededStyle: CSSProperties = {
    color: "red",
    fontWeight: "bold"
};

// Determines the maximum sensor limit based on device serial number
const getSensorLimit = (deviceSerialNumber?: string): number => {
    if (deviceSerialNumber &&
        deviceSerialNumber.length === 5 &&
        deviceSerialNumber.charAt(0) === "3") {
        return 5000; // Sensor Limit for WAND V3
    }
    return 500; // Sensor Limit for WAND V2
};

export default class SensorSelectModalPanel extends Component<SensorSelectModalPanelProps, SensorSelectModalPanelState> {
    constructor(props: SensorSelectModalPanelProps) {
        super(props);

        // Initialize with any pre-selected sensors from props
        this.state = {
            selectedSensors: []
        };
    }

    componentDidMount() {
        // We need to initialize selectedSensors based on the already selected sensorIds
        this.initializeSelectedSensors();
    }

    componentDidUpdate(prevProps: SensorSelectModalPanelProps) {
        // If the modal is newly shown or if selectedIds have changed,
        // we need to re-initialize the selected sensors
        if ((!prevProps.shouldBeShown && this.props.shouldBeShown) ||
            (this.props.selectedIds !== prevProps.selectedIds)) {
            this.initializeSelectedSensors();
        }
    }

    // Initialize selectedSensors based on the selectedIds passed through props
    private initializeSelectedSensors() {
        // Find sensors in the rootSensorGroup that match our selectedIds
        if (this.props.rootSensorGroup) {
            const selectedIds = this.props.selectedIds || [];
            if (selectedIds.length > 0) {
                const foundSensors = this.findSensorsById(this.props.rootSensorGroup, selectedIds);
                if (foundSensors.length > 0) {
                    this.setState({ selectedSensors: foundSensors });
                }
            }
        }
    }

    // Helper function to find sensors by their IDs in the sensor group hierarchy
    // This can be used if we need to look up sensor details by ID in the future
    private findSensorsById(sensorGroup: Sensorgroup, sensorIds: string[]): Sensor[] {
        let foundSensors: Sensor[] = [];

        // Check sensors at this level
        if (sensorGroup.sensors) {
            const matchingSensors = sensorGroup.sensors.filter(s => sensorIds.includes(s.id));
            foundSensors = [...foundSensors, ...matchingSensors];
        }

        // Check child groups recursively
        if (sensorGroup.children) {
            for (const childGroup of sensorGroup.children) {
                const sensorsInChild = this.findSensorsById(childGroup, sensorIds);
                foundSensors = [...foundSensors, ...sensorsInChild];
            }
        }

        return foundSensors;
    }

    render() {
        const { onClose, shouldBeShown } = this.props;

        return (
            <ModalPanel
                title={this.title()}
                shouldBeShown={shouldBeShown}
                onClose={() => onClose()}
                content={this.form()}
                shouldApplyPaddingToContent={false}
            />
        );
    }

    title() {
        if (this.props.title) {
            return this.props.title;
        } else if (this.props.multiSelect) {
            return "Select sensors";
        } else {
            return "Select sensor";
        }
    }

    private form() {
        return (
            <div>
                {this.sensorCountDisplay()}
                {this.deviceCompatibilityMessage()}
                {this.sensorTreePanel()}
                {this.footer()}
            </div>
        );
    }

    private sensorCountDisplay() {
        const { selectedSensors } = this.state;
        const count = selectedSensors.length;
        const sensorLimit = getSensorLimit(this.props.deviceSerialNumber);
        const isExceeded = count > sensorLimit;

        return (
            <div style={sensorCountStyle}>
                <span style={isExceeded ? sensorLimitExceededStyle : undefined}>
                    {isExceeded
                        ? `Selected sensors (${count}/${sensorLimit}) - Exceeds maximum limit!`
                        : `Selected sensors: ${count}/${sensorLimit}`
                    }
                </span>
            </div>
        );
    }

    private sensorTreePanel() {
        if (this.props.rootSensorGroup == undefined) {
            return null;
        }

        return (
            <SensorSelectPanel
                rootSensorGroup={this.props.rootSensorGroup}
                onSensorSelect={(_ids: string[], sensors: Sensor[]) => this.setState({ selectedSensors: sensors })}
                multiSelect={this.props.multiSelect}
                selectedIds={this.state.selectedSensors.map(s => s.id)}
                allowedRfidPrefixes={this.props.allowedRfidPrefixes}
            />
        );
    }

    private footer() {
        const { selectedSensors } = this.state;
        const sensorLimit = getSensorLimit(this.props.deviceSerialNumber);
        const isExceeded = selectedSensors.length > sensorLimit;

        return (
            <div style={containerStyle}>
                <div style={rightHandButtonsStyle}>
                    <ButtonsFormFooter
                        buttons={[
                            <Button
                                key="ok"
                                precedence="primary"
                                icon={<AddIcon />}
                                label={this.props.okLabel || "ADD"}
                                onClick={() => this.onOKButtonClick()}
                                isDisabled={selectedSensors.length === 0 || isExceeded}
                            />,
                            (
                                this.props.hideNoSensorOption ? null :
                                    <Button
                                        key="no_sensor"
                                        label="No Sensor"
                                        onClick={() => this.onNoSensorButtonClick()}
                                    />
                            ),
                            <CancelButton
                                key="cancel"
                                onClick={() => this.props.onClose()}
                            />
                        ]}
                    />
                </div>
            </div>
        );
    }

    private onOKButtonClick() {
        this.props.onSensorSelect(this.state.selectedSensors);
        this.props.onClose();
    }

    private onNoSensorButtonClick() {
        this.props.onSensorSelect([]);
        this.props.onClose();
    }

    private deviceCompatibilityMessage() {
        if (this.props.deviceCompatibilityMessage) {
            return (
                <div style={{ 
                    padding: "10px", 
                    backgroundColor: "#e8f4fd", 
                    border: "1px solid #bee5eb", 
                    borderRadius: "4px", 
                    marginBottom: "10px",
                    color: "#0c5460"
                }}>
                    <i className="fa fa-info-circle" style={{ marginRight: "8px" }}></i>
                    {this.props.deviceCompatibilityMessage}
                </div>
            );
        }
        return null;
    }
}
